import './TagRow.scss';

import { IconClose } from '@hulu-react-style-components/icons';
import { bem } from 'mission-control-common-components';
import React from 'react';

import Dropdown from '../../../../../../common/Dropdown';
import type { AdTagType, EventType, UNRECOGNIZED } from '../../../../../../configs';
import type { TagSheetTag } from '../../types';
import type { TagValidationError } from '../../validation/types';
import EditableUrlField from '../EditableUrlField/EditableUrlField';
import useTagRow from './useTagRow';

const [, element] = bem('line-items-tag-row');

type TagRowProps = {
  tag: TagSheetTag;
  siblingTags: TagSheetTag[];
  onRemove: () => void;
  onEditTag: (updatedTag: TagSheetTag) => void;
  error?: TagValidationError;
  lineItemBillableEvent?: string | null;
  readonly?: boolean;
};

const TagRow = ({
  tag,
  siblingTags,
  onRemove,
  onEditTag,
  error,
  lineItemBillableEvent,
  readonly = false,
}: TagRowProps): React.JSX.Element => {
  const {
    showPosition,
    tagTypeOptions,
    selectedTypeOption,
    positionOptions,
    selectedPositionOption,
    isPositionDisabled,
    handleTypeChange,
    handlePositionChange,
    handleTagUrlSave,
  } = useTagRow({ tag, siblingTags, onEditTag, lineItemBillableEvent });

  return (
    <tr className="line-items-tag-row">
      <td className={element('indent')} />
      <td className={element('spacer')} colSpan={2} />
      <td className={element('content')} colSpan={5}>
        <div className={element('fields')}>
          <span className={element('field', 'type')}>
            <span className={element('label')}>Type</span>
            <span className={element('type-wrapper')}>
              <Dropdown<AdTagType | typeof UNRECOGNIZED>
                classNameModifier={error?.type ? 'tag-sheet-tag-type tag-sheet-tag-type--error' : 'tag-sheet-tag-type'}
                isSearchable={false}
                options={tagTypeOptions}
                value={selectedTypeOption}
                onChange={handleTypeChange}
                isDisabled={readonly}
                menuPortalTarget={document.body}
              />
              {error?.type && <span className={element('type-error')}>{error.type}</span>}
            </span>
          </span>
          <span className={element('field', 'tag-url')}>
            <span className={element('label')}>Tag</span>
            <EditableUrlField
              value={tag.tagUrl}
              truncate={!readonly}
              placeholder="Click to add tag URL..."
              onSave={handleTagUrlSave}
              testId="tag-url-field"
              error={!!error?.tagUrl}
              errorMessage={tag.tagUrl ? error?.tagUrl : undefined}
              readonly={readonly}
            />
          </span>
          {showPosition ? (
            <span className={element('field', 'position')}>
              <span className={element('label')}>Position</span>
              <span className={element('position-wrapper')}>
                <Dropdown<EventType | typeof UNRECOGNIZED>
                  classNameModifier={
                    error?.position ? 'tag-sheet-tag-position tag-sheet-tag-position--error' : 'tag-sheet-tag-position'
                  }
                  isSearchable={false}
                  options={positionOptions}
                  value={selectedPositionOption}
                  onChange={handlePositionChange}
                  isDisabled={readonly || isPositionDisabled}
                  menuPortalTarget={document.body}
                />
                {error?.position && <span className={element('position-error')}>{error.position}</span>}
              </span>
            </span>
          ) : (
            <span className={element('field', 'position-placeholder')} />
          )}
        </div>
      </td>
      <td className={element('action')}>
        {!readonly && (
          <IconClose className={element('close-icon')} onClick={onRemove} data-testid="tag-remove-button" />
        )}
      </td>
    </tr>
  );
};

export default TagRow;
